'use client'

import { useTranslation } from 'react-i18next'
import { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Bounce, toast } from 'react-toastify'
import { useRouter } from 'next/router'

import NoSleep from 'nosleep.js'

import {
  ConnectionError,
  ConnectionErrorReason,
  ConnectionState,
  DeviceUnsupportedError,
  DisconnectReason,
  ExternalE2EEKeyProvider,
  LoggerNames,
  Participant,
  RemoteParticipant,
  Room,
  RoomConnectOptions,
  RoomEvent,
  RoomOptions,
  VideoCodec,
  VideoPreset,
  VideoPresets,
  setLogLevel,
} from 'livekit-client'
import { DataPacket_Kind, ParticipantPermission, VideoGrant } from 'livekit-server-sdk'
import { loadUserChoices, saveUserChoices } from '@livekit/components-core'

import axios, { AxiosResponse } from 'axios'

import { LocalUserChoices, SpinnerIcon } from '@/src'
import { VideoConference } from '@/src'
import { randomString } from '@/src/lib/client-utils'
import {
  getParticipantCandidateKey,
  getTargetedRecorderIdentity,
  readRecordingTarget,
  shouldIncludeParticipantInTargetRecording,
} from '@/src/lib/recordingTarget'

import { ISetCrmMeetLinkData, ISetCrmMeetLinkRequest, Res } from '@/src/types/app.type'
import {
  TSIPParticipantBodyRequest,
  ISIPParticipantPostResponse,
  ISIPOutboundTrunkPostResponse,
  TSIPOutboundTrunkBodyRequest,
} from '@/src/types/sip.type'

import { LiveKitRoom, Toast, formatChatMessageLinks } from '@/src/components'
import { ConnectionErrorScreen } from '@/src/components/ConnectionErrorScreen'
import { WaitingRoom } from '@/src/components/room/WaitingRoom'
import { Toast as WarningToast } from '@/src/components/Toast/Toast'

import { useToken } from '@/src/hooks'
import { useConnectingSound } from '@/src/hooks/room/connection/useConnectingSound'
import { useReconnectionHandler } from '@/src/hooks/useReconnectionHandler'
import { useGroupTrackSubscriptions } from '@/src/hooks/room/useGroupTrackSubscriptions'
import { useMobileRemoteCameraQualityDowngrade } from '@/src/hooks/room/track/useMobileRemoteCameraQualityDowngrade'
import { useMobileReconnect } from '@/src/hooks/room/useMobileReconnect'
import { useConnectionTelemetry } from '@/src/hooks/room/useConnectionTelemetry'
import { useVpnConnectionHint } from '@/src/hooks/room/useVpnConnectionHint'
import { VpnConnectionNotice } from '@/src/components/VpnConnectionNotice'
import { useScreenShareTelemetry } from '@/src/hooks/room/useScreenShareTelemetry'
import { useScreenShareCodecWatchdog } from '@/src/hooks/room/useScreenShareCodecWatchdog'
import { useScreenShareCodecSwitch } from '@/src/hooks/room/useScreenShareCodecSwitch'
import {
  clearMeetingSession,
  useMeetingSession,
  requestForcePrejoin,
} from '@/src/hooks/room/useMeetingSession'
import { useIsMobile } from '@/src/hooks/useIsMobile'
import { useParticipantTabSwitchProctoring } from '@/src/hooks/proctoring/useParticipantTabSwitchProctoring'

import { createCappedReconnectPolicy } from '@/src/lib/MobileReconnectPolicy'
import { resolveScreenShareProfile } from '@/src/lib/screenShareOptions'
import { ScreenShareProfileContext } from '@/src/context/screenShareProfile-context'

import { decodePassphrase } from '@/src/lib/client-utils'
import { DebugMode, useDebugMode } from '@/src/lib/Debug'
import { clientLogError, clientLogInfo, clientLogWarn } from '@/src/lib/clientLog'

import { setTypeOfMeet } from '@/src/helpers/type-of-meet'
import { getVisibleParticipants } from '@/src/helpers/visibleParticipants'
import { checkIsCorrectLivekitUrl, convertLivekitUrlToWss } from '@/src/helpers/meet-server'

import { ESpecificUsernames } from '@/src/constants/constants'
import { meetAxiosInstance } from '@/src/constants/fetches'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import {
  selectRoomState,
  setLivekitUrl,
  clearLivekitUrl,
  setStatus,
  setStatusCode,
  selectRoomType,
  selectProject,
  selectAvatar,
  selectV,
} from '@/src/store/slices/room'
import { selectBranding, selectIsAdmin, selectMeetCodes, selectSettings } from '@/src/store/slices/meetData'
import { selectTypeOfMeet } from '@/src/store/slices/typeOfMeet'
import { startRecord } from '@/src/store/slices/record'
import { selectAccessToken, setAccessToken } from '@/src/store/slices/token'
import { getParticipantInvitation } from '@/src/lib/companyParticipant'
import {getInterviewAdmissionFailure, type InterviewAdmissionFailure} from '@/src/lib/interviewAdmission'

interface ActiveRoomProps {
  participantConnection?: {identity: string; accessToken: string} | null
  userChoices: LocalUserChoices
  roomName: string
  region?: string
  videoGrant?: VideoGrant
  onLeave?: () => void
  onAdmissionDenied?: (reason: InterviewAdmissionFailure) => void
}

const areEqualProps = (prevProps: ActiveRoomProps, nextProps: ActiveRoomProps) =>
  prevProps.roomName === nextProps.roomName &&
  prevProps.userChoices === nextProps.userChoices &&
  prevProps.videoGrant === nextProps.videoGrant &&
  prevProps.participantConnection === nextProps.participantConnection &&
  prevProps.onAdmissionDenied === nextProps.onAdmissionDenied

const e2eePassphrase = typeof window !== 'undefined' && decodePassphrase(location.hash.substring(1))

const worker =
  typeof window !== 'undefined' &&
  e2eePassphrase &&
  new Worker(new URL('livekit-client/e2ee-worker', import.meta.url))

const e2eeEnabled = !!(e2eePassphrase && worker)
const keyProvider = new ExternalE2EEKeyProvider()

const connectOptions: RoomConnectOptions = {
  autoSubscribe: false,
  peerConnectionTimeout: 10_000,
  websocketTimeout: 10_000,
}

const MAX_RETRIES = 3

/**
 * Паузы перед каждой ручной попыткой входа, в миллисекундах.
 *
 * Когда SDK исчерпал свои две попытки, ActiveRoom снимает комнату и ждёт:
 * три секунды, затем пять, затем семь. Без случайного разброса, чтобы в
 * логах и на экране было видно одну и ту же длительность.
 */
const APP_RETRY_DELAYS_MS = [3_000, 5_000, 7_000]

/**
 * Возвращает паузу перед созданием новой комнаты для ручной попытки входа.
 *
 * Номер попытки считается с единицы. Если попыток больше, чем значений в
 * списке, берём последнюю паузу, а не ноль: иначе следующий вход начнётся
 * сразу после disconnect.
 *
 * @param attempt номер попытки с единицы, как на плашке «1/3»
 */
function delayMsForAppRetry(attempt: number): number {
  const lastDelay = APP_RETRY_DELAYS_MS[APP_RETRY_DELAYS_MS.length - 1]

  return APP_RETRY_DELAYS_MS[attempt - 1] ?? lastDelay
}

/**
 * URL из query `?livekit_url=` считается пином, если это одна строка с хостом.
 *
 * Пин (preview и ручной байпас) нельзя перевыдавать через оркестратор: retry
 * должен остаться на той же ноде.
 */
function isPinnedLivekitUrl(
  livekitUrlFromParams: string | string[] | undefined,
): livekitUrlFromParams is string {
  return typeof livekitUrlFromParams === 'string' && checkIsCorrectLivekitUrl(livekitUrlFromParams)
}

/**
 * Возвращает true, если звонок завершили намеренно, а не оборвала сеть.
 *
 * На такие причины участника нужно отправить на главную: комната уже
 * закрыта, и повторный вход только запутает.
 */
function isGracefulDisconnectReason(reason?: DisconnectReason): boolean {
  return (
    reason === DisconnectReason.SERVER_SHUTDOWN ||
    reason === DisconnectReason.PARTICIPANT_REMOVED ||
    reason === DisconnectReason.ROOM_DELETED ||
    reason === DisconnectReason.ROOM_CLOSED
  )
}

/**
 * Возвращает true, если разрыв похож на сбой сети или ICE-агента.
 *
 * Сюда же относится отсутствие причины: так приходит onError, когда
 * первичный connect падает раньше события Disconnected.
 */
function isNetworkFailureReason(reason?: DisconnectReason): boolean {
  return (
    reason === undefined ||
    reason === DisconnectReason.UNKNOWN_REASON ||
    reason === DisconnectReason.STATE_MISMATCH ||
    reason === DisconnectReason.JOIN_FAILURE ||
    reason === DisconnectReason.SIGNAL_CLOSE ||
    reason === DisconnectReason.CONNECTION_TIMEOUT ||
    reason === DisconnectReason.MEDIA_FAILURE
  )
}

/**
 * Возвращает true, если разрыв уже обрабатывают SDK или сам пользователь.
 *
 * CLIENT_INITIATED — выход из звонка, MIGRATION — смена региона LiveKit,
 * DUPLICATE_IDENTITY — вторая вкладка с тем же человеком. Их нельзя
 * ни ретраить, ни показывать как ошибку подключения.
 */
function shouldIgnoreDisconnectReason(reason?: DisconnectReason): boolean {
  return (
    reason === DisconnectReason.CLIENT_INITIATED ||
    reason === DisconnectReason.MIGRATION ||
    reason === DisconnectReason.DUPLICATE_IDENTITY
  )
}

/**
 * Возвращает true, если на экране зал ожидания, а не сама встреча.
 *
 * Это то же условие, по которому ActiveRoom рисует WaitingRoom вместо
 * VideoConference. По нему решаем, вести человека на форму входа или
 * на сайт компании: крестик орга и «Назад» из зала не должны выглядеть
 * как конец звонка.
 */
function isWaitingRoomGuestView(params: {
  isWaitingRoom: boolean
  bypassWaitingRoom: boolean
  roomAccess: boolean
  isAgent?: boolean
}): boolean {
  return (
    params.isWaitingRoom &&
    !params.bypassWaitingRoom &&
    !params.roomAccess &&
    !params.isAgent
  )
}

/** Опции возврата на форму входа: rejected показывает тост отклонения. */
type ReturnToPreJoinOptions = {
  rejected?: boolean
}

/**
 * Возвращает, включены ли камера и микрофон на экране входа.
 *
 * Submit в зал ожидания всегда шлёт false, иначе клиент пытается сразу
 * публиковать треки. Настоящий выбор к этому моменту уже в localStorage.
 */
function getPrejoinMediaEnabled(userChoices: LocalUserChoices): {
  videoEnabled: boolean
  audioEnabled: boolean
} {
  if (typeof window === 'undefined') {
    return {
      videoEnabled: !!userChoices.videoEnabled,
      audioEnabled: !!userChoices.audioEnabled,
    }
  }

  const persisted = loadUserChoices()

  return {
    videoEnabled: persisted.videoEnabled || !!userChoices.videoEnabled,
    audioEnabled: persisted.audioEnabled || !!userChoices.audioEnabled,
  }
}

/**
 * Достаёт причину разрыва из ошибки connect, если LiveKit её туда положил.
 *
 * onError сам по себе приходит без DisconnectReason. Если подставить
 * undefined, повторный вход начнётся даже для DUPLICATE_IDENTITY.
 */
function disconnectReasonFromError(error: Error): DisconnectReason | undefined {
  if (!(error instanceof ConnectionError)) {
    return undefined
  }

  switch (error.reason) {
    case ConnectionErrorReason.LeaveRequest:
      return typeof error.context === 'number' ? (error.context as DisconnectReason) : undefined

    case ConnectionErrorReason.Cancelled:
      return DisconnectReason.CLIENT_INITIATED

    case ConnectionErrorReason.NotAllowed:
      return DisconnectReason.USER_REJECTED

    case ConnectionErrorReason.ServerUnreachable:
      return DisconnectReason.JOIN_FAILURE

    case ConnectionErrorReason.Timeout:
      return DisconnectReason.CONNECTION_TIMEOUT

    default:
      return DisconnectReason.UNKNOWN_REASON
  }
}

/**
 * Читает булев флаг из sessionStorage, не падая на битом значении.
 *
 * Раньше строка парсилась напрямую: одно испорченное значение в хранилище
 * роняло рендер всей встречи.
 *
 * @param key - ключ в sessionStorage, например isCurator
 */
function readSessionFlag(key: string): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false
  }

  try {
    return JSON.parse(sessionStorage.getItem(key) || 'false') === true
  } catch {
    return false
  }
}

/**
 * Разбирает JSON-пакет из data-канала, возвращая null на битом payload.
 *
 * @param payload - сырые байты из RoomEvent.DataReceived
 */
function readJsonPayload(payload: Uint8Array): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(new TextDecoder().decode(payload))

    return parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/**
 * Уводит на домашнюю страницу брендинга, проверяя схему адреса.
 *
 * Адрес приходит из настроек брендинга, поэтому проверяем, что это http(s):
 * иначе javascript- или data-схема превратила бы выход из встречи в переход
 * по чужой ссылке.
 *
 * @param url - адрес из настроек брендинга
 */
function redirectToBrandingHome(url: string): void {
  try {
    const target = new URL(url, window.location.origin)

    if (target.protocol !== 'http:' && target.protocol !== 'https:') {
      window.location.assign('/')

      return
    }

    window.location.assign(target.toString())
  } catch {
    window.location.assign('/')
  }
}

export const ActiveRoom = memo(function MemoActiveRoom({
  roomName,
  userChoices,
  videoGrant,
  participantConnection,
  onLeave,
  onAdmissionDenied,
}: ActiveRoomProps) {
  const { t } = useTranslation()

  const router = useRouter()
  const { asPath: path, isReady: isRouterReady, query } = router
  const participantInvite = getParticipantInvitation(query)
  const isCompanyParticipant = Boolean(participantInvite)
  const {
    hq,
    codec,
    livekit_url: livekitUrlFromParams,
    phone: phoneFromQuery,
    amo_card_id,
    isolate: isolateQuery,
    recordingCandidateKey: recordingCandidateKeyFromQuery,
    recordingGroupId: recordingGroupIdFromQuery,
    nostats: noStatsParam,
  } = query
  // Диагностика WebRTC включена по умолчанию: она нужна в момент инцидента,
  // а не после него, поэтому флаг только выключает её
  const noStats = (Array.isArray(noStatsParam) ? noStatsParam[0] : noStatsParam) === '1'
  const isolate = isCompanyParticipant ? undefined : isolateQuery
  const recordingTarget = useMemo(
    () =>
      readRecordingTarget({
        recordingCandidateKey: recordingCandidateKeyFromQuery,
        recordingGroupId: recordingGroupIdFromQuery,
      }),
    [recordingCandidateKeyFromQuery, recordingGroupIdFromQuery],
  )

  const [roomAccess, setRoomAccess] = useState<boolean>(videoGrant?.canSubscribe)
  const roomAccessRef = useRef(Boolean(videoGrant?.canSubscribe))
  const [currentToken, setCurrentToken] = useState<string | undefined>(undefined)
  const [roomServerReady, setRoomServerReady] = useState(false)
  const [previousBlockUserMic, setPreviousBlockUserMic] = useState<boolean | null>(null)
  const { meet_id } = useAppSelector(selectMeetCodes) ?? {}
  const settings = useAppSelector(selectSettings)
  const { wait_room: isWaitingRoom, is_join_sound_enabled } = settings

  // Фиксируем профиль при входе: обновление API или URL не перезапускает демонстрацию.
  const [screenShareProfile] = useState(() => resolveScreenShareProfile(settings, hq))
  const selectedTypeOfMeet = useAppSelector(selectTypeOfMeet)
  const storedIsAdmin = useAppSelector(selectIsAdmin)
  const isAdmin = !isCompanyParticipant && storedIsAdmin
  const roomType = useAppSelector(selectRoomType)
  const project = useAppSelector(selectProject)
  const avatar = useAppSelector(selectAvatar)
  const v = useAppSelector(selectV)
  const companySlug = Array.isArray(query.companySlug) ? query.companySlug[0] : query.companySlug
  const isSamaletTextAssistant = companySlug === 'samalet' && v === 'hr'
  const shouldBypassWaitingRoomForIsolate =
    !isCompanyParticipant && typeof isolate === 'string' && !isSamaletTextAssistant
  const shouldUseWaitingRoom = !isCompanyParticipant && (isWaitingRoom || v === 'hr')
  const accessToken = useAppSelector(selectAccessToken)
  const { url_log_out } = useAppSelector(selectBranding) ?? {}
  const homeUrl = url_log_out || 'https://proofix.ru'
  const dispatch = useAppDispatch()
  const isElevatingFromWaitingRoomRef = useRef(false)
  // Пока true, handleDisconnected не должен уводить на сайт компании:
  // disconnect мы вызвали сами, чтобы вернуться на форму входа.
  const isReturningToPreJoinRef = useRef(false)
  // Пока true, SDK не должен сам поднимать соединение: человек ушёл
  // кнопкой «Назад» или его отклонил организатор. Иначе гость снова
  // появится в списке зала ожидания у организатора.
  const intentionalDisconnectRef = useRef(false)

  const handleAdmissionDenied = useCallback((reason: InterviewAdmissionFailure) => {
    isReturningToPreJoinRef.current = true
    onAdmissionDenied?.(reason)
  }, [onAdmissionDenied])

  const [retryCount, setRetryCount] = useState(0)
  const [showErrorScreen, setShowErrorScreen] = useState(false)
  const [roomKey, setRoomKey] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)
  // Ref дублирует state, потому что обработчик disconnect должен прочитать флаг сразу, не дожидаясь следующего рендера
  const isRetryingRef = useRef(false)
  // Агент шлёт room-closing чуть раньше Disconnected, и этот флаг не даёт принять такой разрыв за сетевой сбой
  const isRoomClosingRef = useRef(false)
  // Ref нужен, чтобы async-retry не опирался на устаревший retryCount из замыкания
  const retryCountRef = useRef(0)
  // Таймер паузы и флаг монтирования: после ухода со страницы не создаём новую комнату
  const retryDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isActiveRoomMountedRef = useRef(true)

  const identity = useMemo(() => {
    if (isCompanyParticipant) return participantConnection?.identity
    if (videoGrant?.recorder && !isRouterReady) return undefined

    if (videoGrant?.recorder && recordingTarget.recordingCandidateKey) {
      return getTargetedRecorderIdentity(recordingTarget.recordingCandidateKey)
    }

    if (path.includes('director')) {
      return randomString(10)
    }

    if (typeof localStorage !== 'undefined') {
      const identity = localStorage.getItem('identity')?.trim()

      if (identity?.length > 0) {
        return identity
      }
    }

    return randomString(10)
  }, [isRouterReady, path, recordingTarget.recordingCandidateKey, videoGrant?.recorder, isCompanyParticipant, participantConnection?.identity])

  const userChoicePhone = useMemo(() => {
    if (isCompanyParticipant) return ''
    const phoneFromChoices = String((userChoices as any)?.phone || '').trim()
    const rawPhone = query.user_phone ?? phoneFromQuery
    const phoneFromUrl = Array.isArray(rawPhone) ? '' : String(rawPhone || '').trim()
    return phoneFromChoices || phoneFromUrl
  }, [phoneFromQuery, query.user_phone, userChoices, isCompanyParticipant])

  const isCuratorFromUrl = typeof query.isCurator === 'string' && query.isCurator !== 'false'
  const isCuratorFromSession = readSessionFlag('isCurator')
  const isCurator = !isCompanyParticipant && (isCuratorFromUrl || isCuratorFromSession)

  const tokenOptions = useMemo(
    () => ({
      userInfo: {
        identity: isCompanyParticipant ? undefined : identity,
        ...(project !== null && project !== undefined && { project }),
        ...(v !== null && v !== undefined && { v }),
        ...(roomType !== null && roomType !== undefined && {roomType}),
        name: userChoices.username,
        ...(userChoicePhone && { phone: userChoicePhone }),
        canPublish:
          shouldBypassWaitingRoomForIsolate || isAdmin
            ? true
            : videoGrant?.canPublish ?? true,
        canSubscribe:
          shouldBypassWaitingRoomForIsolate || isAdmin
            ? true
            : videoGrant?.canSubscribe ?? false,
        hidden: videoGrant?.hidden ?? false,
        roomAdmin: Boolean(videoGrant?.roomAdmin) || isAdmin,
        isAdmin,
        agent: videoGrant?.agent ?? false,
        recorder: videoGrant?.recorder ?? false,
        ...(videoGrant?.recorder && typeof query.recordingAccess === 'string' && {
          recordingAccess: query.recordingAccess,
        }),
        isCurator,
        ...(recordingTarget.recordingCandidateKey && {
          recordingCandidateKey: recordingTarget.recordingCandidateKey,
        }),
        ...(recordingTarget.recordingGroupId && {
          recordingGroupId: recordingTarget.recordingGroupId,
        }),
      },
    }),
    [
      identity,
      isCompanyParticipant,
      project,
      v,
      roomType,
      shouldBypassWaitingRoomForIsolate,
      userChoices.username,
      userChoicePhone,
      videoGrant?.agent,
      videoGrant?.canPublish,
      videoGrant?.canSubscribe,
      videoGrant?.hidden,
      videoGrant?.roomAdmin,
      videoGrant?.recorder,
      query.recordingAccess,
      isAdmin,
      isCurator,
      recordingTarget.recordingCandidateKey,
      recordingTarget.recordingGroupId,
    ],
  )

  useEffect(() => {
    if (
      isCompanyParticipant ||
      !identity ||
      (videoGrant?.recorder &&
        (!isRouterReady || recordingTarget.recordingCandidateKey))
    ) {
      return
    }

    if (typeof localStorage !== 'undefined' && !localStorage.getItem('identity')) {
      localStorage.setItem('identity', identity)
    }
  }, [identity, isRouterReady, recordingTarget.recordingCandidateKey, videoGrant?.recorder, isCompanyParticipant])

  const outputDeviceId: string = useMemo(
    () => (typeof localStorage !== 'undefined' && localStorage.getItem('output-device-id')) ?? '',
    [],
  )

  useEffect(() => {
    const persistedMedia = getPrejoinMediaEnabled(userChoices)

    if (isCompanyParticipant) return

    // Submit зала ожидания шлёт video/audio false; не затираем выбор с преджойна.
    saveUserChoices({
      ...userChoices,
      videoEnabled: persistedMedia.videoEnabled,
      audioEnabled: persistedMedia.audioEnabled,
    })
  }, [userChoices, isCompanyParticipant])

  const token = useToken(process.env.NEXT_PUBLIC_LK_TOKEN_ENDPOINT, roomName, {
    ...tokenOptions, onAdmissionDenied: handleAdmissionDenied,
  })

  // currentToken побеждает waiting-JWT из useToken: иначе retry после обрыва
  // снова зайдёт в зал ожидания, хотя LiveKit уже выдал publish/subscribe.
  const activeToken = currentToken || participantConnection?.accessToken || token

  const { livekit_url, status, phone, referrer } = useAppSelector(selectRoomState)

  // Определяем тип устройства
  const { isMobileByUserAgent, isInAppBrowser } = useIsMobile()

  // Две быстрые попытки SDK на том же Room: короткие моргания закрываются
  // бесшовно, а настоящий обрыв быстро доходит до нашего retry.
  const reconnectPolicy = useMemo(
    () =>
      createCappedReconnectPolicy({
        shouldReconnect: () => !intentionalDisconnectRef.current,
      }),
    [],
  )

  const roomOptions = useMemo((): RoomOptions => {
    let videoCodec: VideoCodec | undefined = (
      Array.isArray(codec) ? codec[0] : codec ?? 'vp9'
    ) as VideoCodec

    if (e2eeEnabled && (videoCodec === 'av1' || videoCodec === 'vp9')) {
      videoCodec = undefined
    }

    setLogLevel('debug', LoggerNames.E2EE)

    // Кастомный пресет 854x480 для мобильных устройств (битрейт ~750kbps)
    const mobileVideoPreset = new VideoPreset(854, 480, 750_000, 30)
    // Для мобильных устройств используем 854x480 для снижения нагрузки
    const mobileVideoSimulcastLayers = [mobileVideoPreset, VideoPresets.h360, VideoPresets.h216]
    const desktopVideoSimulcastLayers =
      hq === 'true'
        ? [VideoPresets.h1080, VideoPresets.h720]
        : [VideoPresets.h720, VideoPresets.h540, VideoPresets.h216]

    // На мобильных устройствах не приостанавливаем видео в фоне,
    // чтобы избежать разрыва соединения
    const isMobile = isMobileByUserAgent || isInAppBrowser

    return {
      videoCaptureDefaults: {
        deviceId: userChoices.videoDeviceId ?? undefined,
        // Ограничиваем разрешение захвата камеры на мобильных (854x480)
        ...(isMobileByUserAgent && { resolution: { width: 854, height: 480, frameRate: 30 } }),
      },
      publishDefaults: {
        dtx: false,
        // H.264 назначается только при публикации ScreenShare; камеры остаются на VP8.
        screenShareEncoding: screenShareProfile.publishOptions.screenShareEncoding,
        screenShareSimulcastLayers: screenShareProfile.publishOptions.screenShareSimulcastLayers,
        videoSimulcastLayers: isMobileByUserAgent ? mobileVideoSimulcastLayers : desktopVideoSimulcastLayers,
        red: !e2eeEnabled,
        videoCodec: 'vp8',
      },
      audioCaptureDefaults: {
        deviceId: userChoices.audioDeviceId ?? undefined,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      audioOutput: {
        deviceId: outputDeviceId,
      },
      // На мобильных не приостанавливаем видео в фоне, чтобы соединение не разрывалось
      adaptiveStream: { pixelDensity: 'screen', pauseVideoInBackground: !isMobile },
      dynacast: true,
      stopLocalTrackOnUnpublish: false, // удалить локальный трэк после публикации
      disconnectOnPageLeave: false,
      webAudioMix: false, // весь звук в одном миксе, без дорожек
      // Используем кастомную политику переподключения с увеличенным количеством попыток
      reconnectPolicy,
      e2ee: e2eeEnabled
        ? {
            keyProvider,
            worker,
          }
        : undefined,
    }
  }, [userChoices, hq, codec, outputDeviceId, isMobileByUserAgent, isInAppBrowser, reconnectPolicy, screenShareProfile])

  const room = useMemo(() => new Room(roomOptions), [roomOptions, roomKey])

  // State живёт выше LiveKitRoom: app-retry меняет Room, но не начинает новый заход.
  const vpnHint = useVpnConnectionHint({
    room,
    roomName,
    accessToken: activeToken,
    enabled: !videoGrant?.agent && !videoGrant?.recorder,
  })
  const reportVpnNetworkProblem = vpnHint.reportNetworkProblem

  useEffect(() => {
    const noSleep = new NoSleep()

    noSleep?.enable().catch((error) => {
      console.error(error)
    })
  }, [])

  // Хук для автоматического переподключения на мобильных устройствах
  // При переходе в фоновый режим и возврате проверяет соединение и переподключается
  useConnectionTelemetry({
    room,
    roomName,
    identity: identity ?? '',
    enabled: true,
    retryCount,
  })

  useDebugMode({ room })

  useScreenShareTelemetry({ room, enabled: !noStats })
  useScreenShareCodecWatchdog({ room, enabled: !noStats })
  useScreenShareCodecSwitch({ room, enabled: !noStats })

  // Хук для сохранения контекста встречи (для авто-входа при полной перезагрузке)
  // Когда Android/Chrome выгружает вкладку и потом пользователь возвращается
  useMeetingSession({
    room,
    roomName,
    userChoices,
    enabled: true,
  })
  
  // Используем хук для автоматического управления подписками на треки по группам
  // Хук подписывается на события комнаты и автоматически управляет подписками:
  // - При подключении подписывается только на треки участников из той же группы
  // - При появлении нового трека проверяет группу и подписывается, если группы совпадают
  // - При изменении группы локального участника переподписывается на новую группу
  // - При изменении группы удаленного участника обновляет подписки соответственно
  useGroupTrackSubscriptions(room)

  const isHqRoom = Array.isArray(hq) ? hq[0] === 'true' : hq === 'true'

  // На телефоне чужие камеры берём на слой ниже размера плитки,
  // чтобы устройство меньше грелось. Комнаты hq не трогаем.
  useMobileRemoteCameraQualityDowngrade(
    room,
    (isMobileByUserAgent || isInAppBrowser) && !isHqRoom,
  )

  // Хук для проктеринга кандидата в интервью-комнатах
  // При уходе вкладки в фон отправляет событие о смене вкладки
  useParticipantTabSwitchProctoring({
    room,
    roomName,
    roomType,
    meetId: meet_id,
    participantName: userChoices?.username,
    isAdmin,
  })

  // Старые клиенты ещё могут прислать admin_commands; перезаход из них больше не запускаем
  useReconnectionHandler({
    room,
    identity: identity ?? '',
  })

  /**
   * После серверного впуска включает камеру с микрофоном по выбору с преджойна
   * и сохраняет elevated JWT.
   *
   * На self-hosted LiveKit смена прав не подменяет токен сессии. Без нового JWT
   * любой явный retry снова зайдёт в зал ожидания. Камеру в waiting нельзя было
   * опубликовать, поэтому после canPublish=true её нужно включить руками — даже
   * если запрос JWT не удался: права публикации в этой сессии уже выданы.
   */
  const elevateAfterWaitingRoomAdmit = useCallback(async () => {
    if (isElevatingFromWaitingRoomRef.current) {
      return
    }

    if (!livekit_url || !identity) {
      clientLogWarn('waiting-room-admit guest skip: missing livekit_url or identity', {
        room: roomName,
        identity,
        hasLivekitUrl: Boolean(livekit_url),
      })

      return
    }

    isElevatingFromWaitingRoomRef.current = true

    const permissions = room.localParticipant.permissions

    clientLogInfo('waiting-room-admit guest permissions became elevated', {
      room: roomName,
      identity,
      canPublish: permissions?.canPublish,
      canSubscribe: permissions?.canSubscribe,
    })

    let tracksReady = false
    let hasElevatedToken = false

    const prejoinMedia = getPrejoinMediaEnabled(userChoices)

    try {
      let blockUserMic = false

      if (room.metadata) {
        try {
          const parsed = JSON.parse(room.metadata) as { blockUserMic?: boolean }

          blockUserMic = parsed.blockUserMic === true
        } catch {
          blockUserMic = false
        }
      }

      const microphoneEnabled = prejoinMedia.audioEnabled && !blockUserMic

      await Promise.all([
        room.localParticipant.setCameraEnabled(prejoinMedia.videoEnabled),
        room.localParticipant.setMicrophoneEnabled(microphoneEnabled),
      ])

      tracksReady = true

      clientLogInfo('waiting-room-admit guest tracks ready', {
        room: roomName,
        identity,
        canPublish: room.localParticipant.permissions?.canPublish,
        canSubscribe: room.localParticipant.permissions?.canSubscribe,
        canPublishSources: room.localParticipant.permissions?.canPublishSources,
        videoEnabled: prejoinMedia.videoEnabled,
        audioEnabled: microphoneEnabled,
        blockUserMic,
      })
    } catch (error) {
      clientLogError('waiting-room-admit guest tracks failed', {
        room: roomName,
        identity,
        error: error instanceof Error ? error.message : String(error),
      })
    }

    try {
      const elevatePhone = isCompanyParticipant ? '' : userChoicePhone || phone

      const response = await fetch(
        isCompanyParticipant ? '/api/company_participant_token' : `${window.origin}/api/generate_elevated_token?livekit_url=${livekit_url}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${activeToken}`,
          },
          body: JSON.stringify({
            ...(isCompanyParticipant && {participantInvite, accessToken: activeToken}),
            roomName,
            identity,
            name: userChoices.username,
            ...(elevatePhone && { phone: elevatePhone }),
            ...(project !== null && project !== undefined && { project }),
            ...(v !== null && v !== undefined && { v }),
            ...(referrer !== null && referrer !== undefined && { referrer }),
          }),
        },
      )

      if (!response.ok) {
        throw new Error(`Failed to get elevated token: ${response.status}`)
      }

      const { accessToken: elevatedToken } = (await response.json()) as { accessToken?: string }

      if (!elevatedToken) {
        throw new Error('Elevated token response has no accessToken')
      }

      dispatch(setAccessToken(elevatedToken))
      setCurrentToken(elevatedToken)
      hasElevatedToken = true

      clientLogInfo('waiting-room-admit guest token ready', {
        room: roomName,
        identity,
        tracksReady,
      })
    } catch (error) {
      clientLogError('waiting-room-admit guest elevate failed', {
        room: roomName,
        identity,
        tracksReady,
        error: error instanceof Error ? error.message : String(error),
      })
    }

    if (!tracksReady || !hasElevatedToken) {
      isElevatingFromWaitingRoomRef.current = false
    }
  }, [
    dispatch,
    isCompanyParticipant,
    participantInvite,
    activeToken,
    identity,
    livekit_url,
    phone,
    project,
    referrer,
    room,
    roomName,
    userChoicePhone,
    userChoices.audioEnabled,
    userChoices.username,
    userChoices.videoEnabled,
    v,
  ])

  /**
   * Отключает от LiveKit и возвращает на форму входа в эту же комнату.
   *
   * Раньше «Назад» в зале и крестик орга уводили на сайт компании или
   * оставляли человека на экране ожидания. Сессию чистим после disconnect:
   * хук встречи на Disconnected снова пишет session, и иначе телефон сам
   * вернёт в зал.
   *
   * Перед disconnect снимаем livekit_url и создаём новый Room: иначе
   * LiveKitRoom остаётся смонтированным и SDK поднимает то же соединение.
   *
   * @param options.rejected - показать тост, что организатор не впустил
   */
  const returnToPreJoin = useCallback(
    async (options?: ReturnToPreJoinOptions) => {
      if (isReturningToPreJoinRef.current) {
        return
      }

      isReturningToPreJoinRef.current = true
      intentionalDisconnectRef.current = true

      // Снимаем URL и Room в одном батче, пока disconnect ещё не начался:
      // иначе LiveKitRoom останется смонтированным и снова вызовет connect.
      dispatch(clearLivekitUrl())
      setRoomKey((key) => key + 1)

      if (options?.rejected) {
        toast.info(t('waitingRoomRejected'), {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          theme: 'dark',
        })
      }

      try {
        await room.disconnect()
      } catch (error) {
        console.error('[ActiveRoom] Error disconnecting before PreJoin:', error)
      }

      // Хук сессии на Disconnected снова пишет session; чистим уже после отключения
      clearMeetingSession()

      if ('inWaitingRoom' in query) {
        const nextQuery = { ...query }
        delete nextQuery.inWaitingRoom

        try {
          await router.replace({ query: nextQuery }, undefined, { shallow: true })
        } catch (error) {
          console.error('[ActiveRoom] Error stripping inWaitingRoom query:', error)
        }
      }

      setShowErrorScreen(false)
      dispatch(setStatus('prejoin'))
    },
    [dispatch, query, room, router, t],
  )

  /**
   * Уводит на форму входа полной перезагрузкой страницы — единственное действие
   * кнопки на экране ошибки.
   *
   * Зал ожидания есть не во всех звонках, поэтому ветвление по источнику ошибки
   * убрали: кнопка всегда называется «Войти ещё раз» и всегда перезагружает
   * страницу. Перед перезагрузкой чистим сессию встречи и ставим одноразовый флаг
   * force-prejoin: без него мобильный браузер после reload увидел бы
   * сохранённую сессию и молча затянул человека обратно в звонок, минуя PreJoin.
   * Полная перезагрузка вместо SPA-возврата гарантирует свежий ICE-агент и
   * пустое состояние комнаты: именно этого не хватало при исчерпанных ретраях.
   */
  const rejoinViaReload = useCallback(() => {
    try {
      void room.disconnect()
    } catch (error) {
      console.error('[ActiveRoom] Error disconnecting before reload:', error)
    }

    clearMeetingSession()
    requestForcePrejoin()

    // inWaitingRoom в URL рисует зал ожидания; после явного «Войти ещё раз»
    // человеку нужна обычная форма входа, поэтому параметр стираем — так же,
    // как это делает SPA-возврат returnToPreJoin.
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete('inWaitingRoom')

      window.location.assign(url.toString())
    } catch {
      window.location.reload()
    }
  }, [room])

  /**
   * Запрашивает адрес LiveKit у `/api/start_server` и кладёт его в redux.
   *
   * На обычном входе при 503 показываем тост и возвращаем на преджойн. На
   * автоматическом переподключении тост тот же, но текущий адрес не сбрасываем:
   * иначе ActiveRoom размонтируется, и человек улетит на форму входа посреди retry.
   *
   * @param keepCurrentUrlOnFailure не менять redux-статус, если сервер не ответил новым адресом
   */
  const requestLivekitUrl = useCallback(
    async (
      keepCurrentUrlOnFailure = false,
    ): Promise<{ livekit_url?: string; status?: number } | null> => {
      if (!keepCurrentUrlOnFailure) {
        dispatch(setStatus('loading'))
      }

      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 25000)

        try {
          const response = await fetch(`${window.location.origin}/api/start_server`, {
            method: 'POST',
            body: JSON.stringify({ roomName, roomType, project, avatar,
              candidateName: userChoices.username, phone: userChoicePhone || phone, referrer,
              participantInvite: isCompanyParticipant ? participantInvite : undefined,
              recordingAccess: videoGrant?.recorder ? query.recordingAccess : undefined }),
            signal: controller.signal,
          })
          const res = (await response.json()) as {
            livekit_url: string; status: number; canJoin?: boolean; reason?: string
          }
          if (!isActiveRoomMountedRef.current) return null

          const failure = getInterviewAdmissionFailure(res)
          if (failure) {
            handleAdmissionDenied(failure)
            return null
          }

          dispatch(setStatusCode(res?.status))

          if (res?.status === 503) {
            toast.error(
              (content) => (
                <WarningToast
                  title={content?.toastProps?.ariaLabel}
                  description={content?.data}
                />
              ),
              {
                ariaLabel: t('toast.warning'),
                data: t('hasNoAvailableRoom'),
                role: 'alert',
                position: 'bottom-right',
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: 'dark',
                transition: Bounce,
              },
            )
          }

          if (res?.livekit_url && res?.status !== 503) {
            dispatch(setStatus('fulfilled'))
            dispatch(setLivekitUrl(res.livekit_url))
            setRoomServerReady(true)
          } else if (!keepCurrentUrlOnFailure) {
            dispatch(setStatus('prejoin'))
          }

          return res
        } finally {
          clearTimeout(timeoutId)
        }
      } catch (e) {
        if (!isActiveRoomMountedRef.current) return null
        if (!keepCurrentUrlOnFailure) {
          dispatch(setStatus('rejected'))
        }

        if (e instanceof Error) {
          console.error(`Error with start server: \n${e}`)
        } else {
          console.error(`Unknown error with start server: \n${e}`)
        }

        return null
      }
    },
    [avatar, dispatch, project, roomName, roomType, t, userChoices.username, userChoicePhone,
      phone, referrer, isCompanyParticipant, participantInvite, handleAdmissionDenied,
      videoGrant?.recorder, query.recordingAccess],
  )

  /**
   * Отключает текущий Room и создаёт новый для повторного входа.
   *
   * Без явного disconnect старый объект остаётся живым, потому что в опциях
   * комнаты выключен disconnectOnPageLeave. Новый Room нужен, чтобы браузер
   * поднял свежий ICE-агент. Между disconnect и новым объектом ждём паузу
   * из APP_RETRY_DELAYS_MS: сети и старой сессии на ноде нужно время закрыться.
   * Перед новым Room перезапрашиваем адрес ноды — иначе retry упрётся в ту же
   * сломанную машину, с которой только что не получилось соединиться.
   */
  const retryConnection = useCallback(async () => {
    if (isRetryingRef.current) {
      return
    }

    const nextRetry = retryCountRef.current + 1

    if (nextRetry > MAX_RETRIES) {
      isRetryingRef.current = true
      setIsRetrying(false)
      setShowErrorScreen(true)

      try {
        await room.disconnect()
      } catch (error) {
        console.error('[ActiveRoom] Error disconnecting after retries:', error)
      }

      // После disconnect сессия могла снова записаться, поэтому очищаем её уже после отключения
      clearMeetingSession()

      return
    }

    isRetryingRef.current = true
    setIsRetrying(true)
    retryCountRef.current = nextRetry
    setRetryCount(nextRetry)

    try {
      await room.disconnect()
    } catch (error) {
      console.error('[ActiveRoom] Error disconnecting before retry:', error)
    }

    // Плашка уже на экране: ждём, пока старая сессия на ноде и сеть остынут.
    // LiveKitRoom в это время скрыт, поэтому встреча на паузе выглядит пустой.
    await new Promise<void>((resolve) => {
      retryDelayTimeoutRef.current = setTimeout(() => {
        retryDelayTimeoutRef.current = null
        resolve()
      }, delayMsForAppRetry(nextRetry))
    })

    if (!isActiveRoomMountedRef.current) {
      return
    }

    // Пин через ?livekit_url= не перевыдаём: иначе preview уедет с закреплённой ноды.
    if (!isPinnedLivekitUrl(livekitUrlFromParams)) {
      await requestLivekitUrl(true)
    }

    if (!isActiveRoomMountedRef.current) {
      return
    }

    setRoomKey((key) => key + 1)
    isRetryingRef.current = false
    setIsRetrying(false)
  }, [livekitUrlFromParams, requestLivekitUrl, room])

  // Если уйти со страницы посреди паузы, таймер не должен поднимать новый Room
  useEffect(() => {
    isActiveRoomMountedRef.current = true

    return () => {
      isActiveRoomMountedRef.current = false

      if (retryDelayTimeoutRef.current !== null) {
        clearTimeout(retryDelayTimeoutRef.current)
        retryDelayTimeoutRef.current = null
      }
    }
  }, [])

  // На мобильном хук только ловит возврат из фона и bfcache.
  // Connect сам не вызывает: иначе спорит с retry, который создаёт новый Room.
  useMobileReconnect({
    room,
    enabled: !isRetrying && !showErrorScreen,
    onReconnectNeeded: () => {
      if (intentionalDisconnectRef.current) {
        console.log('[ActiveRoom] Mobile reconnect suppressed: intentional disconnect')

        return
      }

      reportVpnNetworkProblem()
      retryConnection()
    },
  })

  /**
   * Решает, вернуть на форму входа, увести на главную, повторить вход
   * или показать страницу ошибки.
   *
   * Сетевой сбой больше не должен выглядеть как завершение звонка: именно
   * из-за этого участника раньше молча отправляло на proofix.ru. SIP и
   * NotAllowed не ретраим и не редиректим — иначе UI остаётся мёртвым.
   */
  const handleDisconnected = useCallback(
    (reason?: DisconnectReason) => {
      if (isRetryingRef.current) {
        return
      }

      if (isReturningToPreJoinRef.current) {
        return
      }

      const guestStillWaiting = isWaitingRoomGuestView({
        isWaitingRoom,
        bypassWaitingRoom: shouldBypassWaitingRoomForIsolate,
        roomAccess: roomAccessRef.current,
        isAgent: Boolean(videoGrant?.agent),
      })

      if (reason === DisconnectReason.PARTICIPANT_REMOVED && guestStillWaiting) {
        console.log('[ActiveRoom] Rejected from waiting room, returning to PreJoin')
        // Ставим флаг до returnToPreJoin: SDK может спросить политику
        // реконнекта раньше, чем дойдёт await disconnect.
        intentionalDisconnectRef.current = true
        void returnToPreJoin({ rejected: true })

        return
      }

      if (isRoomClosingRef.current || isGracefulDisconnectReason(reason)) {
        console.log(`[ActiveRoom] Room closed gracefully, reason: ${reason}. Redirecting...`)
        redirectToBrandingHome(homeUrl)

        return
      }

      if (isNetworkFailureReason(reason)) {
        console.log(`[ActiveRoom] Network failure, reason: ${reason}. Retrying...`)
        reportVpnNetworkProblem()
        void retryConnection()

        return
      }

      if (shouldIgnoreDisconnectReason(reason)) {
        return
      }

      // Для SIP 11–13 и NotAllowed (LiveKit мапит 4xx в USER_REJECTED) повтор
      // не поможет, а авторедирект на proofix.ru как раз и путал.
      console.log(`[ActiveRoom] Unrecoverable disconnect, reason: ${reason}. Showing error screen`)
      setShowErrorScreen(true)
      clearMeetingSession()
    },
    [
      homeUrl,
      isWaitingRoom,
      reportVpnNetworkProblem,
      retryConnection,
      returnToPreJoin,
      shouldBypassWaitingRoomForIsolate,
      videoGrant?.agent,
    ],
  )

  /**
   * Запускает retry только если connect упал без события Disconnected.
   *
   * useLiveKitRoom зовёт onError и после отказа connect, и при ошибке
   * микрофона. Сам connect перед reject уже шлёт Disconnected с причиной,
   * поэтому повторно разбирать undefined нельзя: иначе retry начнётся
   * даже для DUPLICATE_IDENTITY.
   */
  const handleRoomError = useCallback(
    (error: Error) => {
      if (
        room.state === ConnectionState.Connected ||
        room.state === ConnectionState.Reconnecting
      ) {
        return
      }

      // Disconnected уже классифицировал настоящую причину на этом же сбое
      if (room.state === ConnectionState.Disconnected) {
        return
      }

      if (room.state === ConnectionState.Connecting && !(error instanceof ConnectionError)) {
        return
      }

      handleDisconnected(disconnectReasonFromError(error))
    },
    [handleDisconnected, room],
  )

  // Редирект на ссылку брендирования при завершении комнаты со стороны сервера/агента
  useEffect(() => {
    if (!room) {
      return
    }

    const handleDataReceived = (
      _payload: Uint8Array,
      _participant?: RemoteParticipant,
      _kind?: DataPacket_Kind,
      topic?: string,
    ) => {
      if (topic === 'room-closing') {
        isRoomClosingRef.current = true
        redirectToBrandingHome(homeUrl)
      }
    }

    room.on(RoomEvent.DataReceived, handleDataReceived)
    room.on(RoomEvent.Disconnected, handleDisconnected)

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived)
      room.off(RoomEvent.Disconnected, handleDisconnected)
    }
  }, [homeUrl, handleDisconnected, room])

  // Имя комнаты появляется только после join, поэтому сброс retry нельзя
  // вешать в эффект с проверкой room.name: Connected успеет пройти раньше.
  useEffect(() => {
    const handleConnected = () => {
      retryCountRef.current = 0
      isRetryingRef.current = false
      intentionalDisconnectRef.current = false

      setRetryCount(0)
      setIsRetrying(false)
      setShowErrorScreen(false)
    }

    room.on(RoomEvent.Connected, handleConnected)

    return () => {
      room.off(RoomEvent.Connected, handleConnected)
    }
  }, [room])

  useEffect(() => {
    setRoomServerReady(false)
    const handleGetLivekitUrl = async () => {
      if (isPinnedLivekitUrl(livekitUrlFromParams)) {
        const customLivekitUrl = convertLivekitUrlToWss(livekitUrlFromParams)

        dispatch(setLivekitUrl(customLivekitUrl))
        dispatch(setStatus('fulfilled'))
        setRoomServerReady(true)

        return
      }

      await requestLivekitUrl()
    }

    const timeoutId = setTimeout(handleGetLivekitUrl, 100)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [dispatch, livekitUrlFromParams, requestLivekitUrl])

  useEffect(() => {
    const handleSetTypeOfMeet = async () => {
      if (typeof selectedTypeOfMeet === 'number' && isAdmin) {
        await setTypeOfMeet(room, meet_id, selectedTypeOfMeet)
      }
    }

    room.on(RoomEvent.Connected, handleSetTypeOfMeet)

    return () => {
      room.off(RoomEvent.Connected, handleSetTypeOfMeet)
    }
  }, [selectedTypeOfMeet, meet_id, isAdmin, room])

  const sound = useConnectingSound()

  useEffect(() => {
    const participantConnectedHandle = (participant: Participant) => {
      if (participant.isAgent) return
      if (
        participant.name === ESpecificUsernames.Recorder ||
        participant.name === ESpecificUsernames.Guest
      )
        return // Egress, Director, Guest name

      if (
        room?.localParticipant.permissions?.canPublish &&
        room?.localParticipant.permissions?.canSubscribe &&
        is_join_sound_enabled
      ) {
        sound?.play()
      }
    }

    room.on(RoomEvent.ParticipantConnected, participantConnectedHandle)

    return () => {
      room.off(RoomEvent.ParticipantConnected, participantConnectedHandle)
    }
  }, [isWaitingRoom, room, is_join_sound_enabled, sound])

  useEffect(() => {
    const handleParticipantPermissionsChanged = (prevPermissions: ParticipantPermission) => {
      if (prevPermissions) {
        const nextAccess = Boolean(room.localParticipant.permissions?.canSubscribe)
        roomAccessRef.current = nextAccess
        setRoomAccess(nextAccess)
      }

      const wasWaiting =
        Boolean(prevPermissions) &&
        prevPermissions.canSubscribe === false &&
        prevPermissions.canPublish === false
      const isInside = room.localParticipant.permissions?.canSubscribe === true

      const isSpecialRole =
        Boolean(videoGrant?.agent) ||
        Boolean(videoGrant?.recorder) ||
        Boolean(videoGrant?.roomAdmin) ||
        isAdmin

      if (wasWaiting && isInside && !isSpecialRole) {
        void elevateAfterWaitingRoomAdmit()
      }
    }

    room.localParticipant.on(
      RoomEvent.ParticipantPermissionsChanged,
      handleParticipantPermissionsChanged,
    )

    return () => {
      room.localParticipant.off(
        RoomEvent.ParticipantPermissionsChanged,
        handleParticipantPermissionsChanged,
      )
    }
  }, [
    elevateAfterWaitingRoomAdmit,
    isAdmin,
    room.localParticipant,
    videoGrant?.agent,
    videoGrant?.recorder,
    videoGrant?.roomAdmin,
  ])

  // Apply microphone block for non-admin users based on room metadata
  useEffect(() => {
    if (!room || !livekit_url) return

    const applyMicrophoneBlock = async (metadata: string | undefined) => {
      if (isAdmin) return

      let blockUserMic = false
      if (metadata) {
        try {
          const parsed = JSON.parse(metadata)
          blockUserMic = parsed.blockUserMic === true
        } catch (e) {
          console.error('Error parsing room metadata:', e)
          return
        }
      }

      if (previousBlockUserMic === blockUserMic) {
        return
      }

      setPreviousBlockUserMic(blockUserMic)

      if (blockUserMic && room.localParticipant.identity) {
        try {
          if (room.localParticipant.isMicrophoneEnabled) {
            await room.localParticipant.setMicrophoneEnabled(false)
          }

          const payload = {
            room: roomName,
            participantIdentities: [room.localParticipant.identity],
            grantAccess: false,
            updateRoomMetadata: false,
          }

          await fetch(`${window.origin}/api/block_mute_participants?livekit_url=${livekit_url}`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
              authorization: accessToken || '',
              'Content-Type': 'application/json',
            },
          })

          console.log('Microphone permissions blocked for non-admin user')
        } catch (error) {
          console.error('Error applying microphone block:', error)
        }
      }
    }

    const handleConnected = () => {
      applyMicrophoneBlock(room.metadata)
    }

    const handleMetadataChanged = (metadata: string) => {
      applyMicrophoneBlock(metadata)
    }

    room.on(RoomEvent.Connected, handleConnected)
    room.on(RoomEvent.RoomMetadataChanged, handleMetadataChanged)

    return () => {
      room.off(RoomEvent.Connected, handleConnected)
      room.off(RoomEvent.RoomMetadataChanged, handleMetadataChanged)
    }
  }, [room, livekit_url, roomName, isAdmin, previousBlockUserMic, accessToken])

  useEffect(() => {
    if (e2eeEnabled) {
      keyProvider.setKey(decodePassphrase(e2eePassphrase))
      room.setE2EEEnabled(true).catch((e) => {
        if (e instanceof DeviceUnsupportedError) {
          alert(
            `You're trying to join an encrypted meeting, but your browser does not support it. Please update it to the latest version and try again.`,
          )
          console.error(e)
        }
      })
    }

    const handleDataReceived = (
      payload: Uint8Array,
      _participant?: RemoteParticipant,
      _kind?: DataPacket_Kind,
      topic?: string,
    ) => {
      if (topic !== 'disconnectParticipant') {
        return
      }

      const message = readJsonPayload(payload)

      if (message?.disconnect && room.localParticipant.permissions?.recorder) {
        room.disconnect()
      }
    }

    room.on(RoomEvent.DataReceived, handleDataReceived)

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived)
    }
  }, [room])

  // useEffect, который отвечает за логику старта записи
  useEffect(() => {
    if (!room) return
    // Если это не egress, значит не будем навешивать лишние обработчики
    if (!(videoGrant?.agent && videoGrant?.recorder && path.includes('/egress/'))) return

    let roomHasRealParticipants: boolean = false
    const interval = setInterval(handleCheck, 1000)

    const checkParticipants = () => {
      if (recordingTarget.recordingCandidateKey) {
        let participantGroupMap: Record<string, string | null> | undefined
        try {
          participantGroupMap = room.metadata
            ? JSON.parse(room.metadata).participantGroupMap
            : undefined
        } catch {
          participantGroupMap = undefined
        }

        roomHasRealParticipants = Array.from(room.remoteParticipants.values()).some(
          (participant) =>
            getParticipantCandidateKey(participant) ===
              recordingTarget.recordingCandidateKey &&
            shouldIncludeParticipantInTargetRecording(
              participant,
              recordingTarget,
              participantGroupMap,
            ),
        )
        return
      }

      const visibleParticipants = getVisibleParticipants(room?.remoteParticipants)

      if (visibleParticipants?.length > 0) {
        roomHasRealParticipants = true
      }
    }

    function handleCheck() {
      checkParticipants()

      if (roomHasRealParticipants) {
        console.log('START_RECORDING')
        dispatch(startRecord())

        if (interval) {
          clearInterval(interval)
        }
      }
    }
    setTimeout(handleCheck, 500)

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [
    room,
    path,
    livekit_url,
    identity,
    videoGrant?.agent,
    videoGrant?.recorder,
    userChoices?.username,
    dispatch,
    recordingTarget,
  ])

  console.info('livekit_url - ', livekit_url)

  useEffect(() => {
    const getRoomSid = async () =>
      await room?.getSid()?.catch((error) => {
        console.error(`Error with getting sid of Room: ${error}`)
      })

    if (!livekit_url || !room || !room?.name) return

    const handleConnected = () => {
      clientLogInfo(
        `livekit_url, roomName, roomId: ${livekit_url}, ${room?.name}, ${getRoomSid()}`,
      )
    }

    room?.on(RoomEvent.Connected, handleConnected)

    return () => {
      room?.off(RoomEvent.Connected, handleConnected)
    }
  }, [livekit_url, room, room?.name])

  useEffect(() => {
    if (!room || isCompanyParticipant) return
    if (!phoneFromQuery || Array.isArray(phoneFromQuery)) return

    const createTrunk = async () =>
      await meetAxiosInstance
        .post<
          ISIPOutboundTrunkPostResponse,
          AxiosResponse<ISIPOutboundTrunkPostResponse>,
          TSIPOutboundTrunkBodyRequest
        >(`/api/sip/outbound_trunk?livekit_url=${livekit_url}`)
        .catch((err) => {
          console.error('Error with creating outbound trunk: \n', err)
        })

    const callToParticipant = async (trunkId: string) =>
      await meetAxiosInstance
        .post<
          ISIPParticipantPostResponse,
          AxiosResponse<ISIPParticipantPostResponse>,
          TSIPParticipantBodyRequest
        >(`/api/sip/participant?livekit_url=${livekit_url}`, {
          roomName: room?.name,

          participantIdentity: Math.random().toString().split('.')?.at(1),
          participantName: t('sip.participantName'),

          phoneNumber: phoneFromQuery,
          trunkId,
        })
        .catch((err) => {
          console.error('Error with creating sip participant: \n', err)
        })

    const handleConnected = async () => {
      try {
        const getRoomSid = async () => await room?.getSid()

        if (!livekit_url || !room?.name || !getRoomSid()) return

        const trunk = await createTrunk()

        if (trunk) {
          void callToParticipant(trunk?.data?.data?.sipTrunkId)
        }
      } catch (error) {
        console.error(`Error with calling to participant: ${error}`)
      }
    }

    room?.on(RoomEvent.Connected, handleConnected)

    return () => {
      room?.off(RoomEvent.Connected, handleConnected)
    }
  }, [livekit_url, room, room?.name, phoneFromQuery, t, isCompanyParticipant])

  useEffect(() => {
    if (!room || isCompanyParticipant) return
    if (!amo_card_id || Array.isArray(amo_card_id)) return

    const handleConnected = async () =>
      await axios
        .post<
          Res<ISetCrmMeetLinkData>,
          AxiosResponse<Res<ISetCrmMeetLinkData>>,
          ISetCrmMeetLinkRequest
        >(`${process.env.NEXT_PUBLIC_PLATFORM_ENDPOINT}/set_crm_meet_link`, {
          code_name_crm: 'amoCrm',
          foreign_meet_id: await room?.getSid(),
          foreign_card_id: amo_card_id,
        })
        .catch((err) => {
          console.error('Error with setting crm with meet link: \n', err)
        })

    room?.on(RoomEvent.Connected, handleConnected)

    return () => {
      room?.off(RoomEvent.Connected, handleConnected)
    }
  }, [amo_card_id, room, isCompanyParticipant])

  return (
    <ScreenShareProfileContext.Provider value={screenShareProfile}>
      <VpnConnectionNotice
        roomName={roomName}
        assessment={vpnHint.assessment}
        shouldWarn={vpnHint.shouldWarn}
        onShown={vpnHint.markWarningShown}
      />

      {status === 'loading' && (
        <Toast className="lk-toast-connection-state">
          <SpinnerIcon className="lk-spinner" />{' '}
          <span style={{ textWrap: 'nowrap' }}>{t('uploadingSettings')}...</span>
        </Toast>
      )}

      {retryCount > 0 && !showErrorScreen && (
        <Toast className="lk-toast-connection-state">
          <SpinnerIcon className="lk-spinner" />{' '}
          <span style={{ textWrap: 'nowrap' }}>
            {t('connectionRetry', { current: retryCount, max: MAX_RETRIES })}
          </span>
        </Toast>
      )}

      {showErrorScreen && (
        <ConnectionErrorScreen buttonLabel={t('tryAgain')} onButtonClick={rejoinViaReload} />
      )}

      {roomServerReady && livekit_url && !isRetrying && !showErrorScreen && (
        <LiveKitRoom
          room={room}
          token={activeToken}
          serverUrl={livekit_url}
          connectOptions={connectOptions}
          video={userChoices?.videoEnabled}
          audio={userChoices?.audioEnabled}
          onDisconnected={onLeave}
          onError={handleRoomError}
        >
          {(roomAccess && room) ||
          videoGrant?.agent ||
          !shouldUseWaitingRoom ||
          shouldBypassWaitingRoomForIsolate ? (
            <VideoConference
              chatMessageFormatter={formatChatMessageLinks}
              hideConnectionStateToast={retryCount > 0}
              hidePoorConnectionToast={vpnHint.vpnSuspected}
            />
          ) : (
            <WaitingRoom
              room={room}
              onLeaveWaitingRoom={() => {
                void returnToPreJoin()
              }}
            />
          )}

          <DebugMode />
        </LiveKitRoom>
      )}
    </ScreenShareProfileContext.Provider>
  )
},
areEqualProps)
